import type { Contentblock } from '@/kontent/content-types/contentblock'
import type { UiInpagealert } from '@/kontent/content-types/ui_inpagealert'
import { getLinkedItems, isRichtextEmpty } from '@/utils'
import type { Elements } from '@kontent-ai/delivery-sdk'
import clsx from 'clsx'
import type { RichtextSectionProps } from '.'
import RichText from '../RichText'
import {
	InPageAlert,
	type InPageAlertType,
} from '../ui/in-page-alert/InPageAlert'

/**
 * Renders a 💠 Web UI In-page alert (`ui_inpagealert`) using the shared
 * NSW Design System in-page alert.
 */
const ALERT_TYPES: InPageAlertType[] = ['info', 'warning', 'error', 'success']

const escapeHtml = (value: string) =>
	value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

/**
 * A DS compact alert has no separate title — the title reads as a bold lead-in
 * to the first sentence, so it is merged into the first paragraph of the rich
 * text. Returns null when the content does not start with a paragraph (list,
 * table, image, embedded component); the caller then falls back to rendering
 * the title as its own bold lead line.
 */
export function withInlineTitleLead(
	html: string,
	title: string,
): string | null {
	const openingParagraph = html.match(/^\s*<p(?:\s[^>]*)?>/i)
	if (!openingParagraph) return null

	const insertAt = openingParagraph.index + openingParagraph[0].length
	return (
		html.slice(0, insertAt) +
		`<strong>${escapeHtml(title)}</strong> ` +
		html.slice(insertAt)
	)
}

export default function UiInpagealertSection({
	linkedItem,
	linkedItems,
	mappings,
	currentPath,
	className,
}: RichtextSectionProps<UiInpagealert>) {
	const contentBlock = getLinkedItems(
		linkedItem.elements.item,
		linkedItems,
	)?.[0] as Contentblock

	// Without a content block there is nothing to show — return null rather
	// than letting the raw Kontent <object> tag reach the browser, which
	// renders as "This plugin is not supported"
	if (!contentBlock) return null

	// Elements can be absent when the page was fetched with an
	// elementsParameter that trims linked-item elements (e.g. stage pages)
	const typeCodename = linkedItem.elements.type?.value?.[0]?.codename
	const alertType = ALERT_TYPES.includes(typeCodename as InPageAlertType)
		? (typeCodename as InPageAlertType)
		: 'info'
	const isCompact =
		linkedItem.elements.display?.value?.[0]?.codename === 'compact'

	const title = contentBlock.elements.title?.value
	const content = contentBlock.elements.content
	const hasContent = content && !isRichtextEmpty(content.value)

	// In compact mode fold the title into the body's first paragraph; keep it
	// as a separate lead line only when that isn't possible
	let richTextElement = content
	let separateTitle = title
	if (isCompact && title && hasContent) {
		const merged = withInlineTitleLead(content.value, title)
		if (merged) {
			richTextElement = {
				...content,
				value: merged,
			} as Elements.RichTextElement
			separateTitle = undefined
		}
	}

	return (
		<InPageAlert
			className={className}
			as={alertType}
			title={separateTitle}
			compact={isCompact}
			data-kontent-item-id={linkedItem.system.id}
		>
			{hasContent ? (
				<div
					className={
						clsx(!isCompact && separateTitle && 'mt-3') || undefined
					}
				>
					<RichText
						richTextElement={richTextElement}
						linkedItems={linkedItems}
						mappings={mappings}
						currentPath={currentPath}
					/>
				</div>
			) : null}
		</InPageAlert>
	)
}
